import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Check,
  CheckCircle2,
  FunctionSquare,
  Hash,
  LoaderCircle,
  Plus,
  Search,
  Sigma,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { FormulaCodeOption } from './FormulaInput';
import FormulaExpressionField, {
  type FormulaExpressionFieldHandle,
} from './FormulaExpressionField';
import {
  buildFormulaEditorValue,
  parseFormulaEditorValue,
  type FormulaRounding,
} from './formulaEditor';

export interface FormulaEditorPreview {
  hours?: number;
  amount?: string;
  error?: string;
}

interface FormulaEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  options: FormulaCodeOption[];
  lineLabel?: string;
  variantName?: string;
  rateLabel?: string;
  initialPreview?: FormulaEditorPreview;
  onPreview?: (formula: string) => Promise<FormulaEditorPreview>;
  onApply: (formula: string) => void;
}

type PreviewState =
  | { status: 'idle' | 'loading' | 'unavailable' }
  | ({ status: 'valid' | 'invalid'; beforeHours?: number } & FormulaEditorPreview);

type RuleFilter = 'all' | 'manual' | 'formula';

const roundingOptions: Array<{
  value: FormulaRounding;
  label: string;
  example: string;
}> = [
  { value: 'none', label: 'Niet afronden', example: '9,5 blijft 9,5 uur' },
  { value: 'nearest', label: 'Dichtstbij', example: '9,5 wordt 10 uur' },
  { value: 'up', label: 'Altijd omhoog', example: '9,1 wordt 10 uur' },
  { value: 'down', label: 'Altijd omlaag', example: '9,9 wordt 9 uur' },
];

const operators = [
  { label: 'Optellen', symbol: '+', token: ' + ' },
  { label: 'Aftrekken', symbol: '−', token: ' - ' },
  { label: 'Vermenigvuldigen', symbol: '×', token: ' * ' },
  { label: 'Delen', symbol: '÷', token: ' / ' },
  { label: 'Percentage', symbol: '%', token: '%' },
  { label: 'Haakjes', symbol: '( )', token: '()', caretBack: 1 },
];

const helpers = [
  {
    label: 'Som van regels',
    description: 'Tel geselecteerde regels op',
    functionName: 'SUM',
    icon: Sigma,
  },
  {
    label: 'Positief getal',
    description: 'Nooit lager dan nul',
    functionName: 'POSITIVE',
    icon: Plus,
  },
  {
    label: 'Laagste waarde',
    description: 'Kies de kleinste uitkomst',
    functionName: 'MIN',
    icon: ArrowDown,
  },
  {
    label: 'Hoogste waarde',
    description: 'Kies de grootste uitkomst',
    functionName: 'MAX',
    icon: ArrowUp,
  },
];

const formatHours = (value: number | undefined) =>
  value === undefined
    ? '—'
    : `${new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(value)} uur`;

const codePattern = (code: string) =>
  new RegExp(`\\b${code.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}\\b`);

export function FormulaEditorDialog({
  open,
  onOpenChange,
  value,
  options,
  lineLabel,
  variantName,
  rateLabel,
  initialPreview,
  onPreview,
  onApply,
}: FormulaEditorDialogProps) {
  const inputRef = useRef<FormulaExpressionFieldHandle>(null);
  const wasOpen = useRef(false);
  const previewRequest = useRef(0);
  // Ouders geven bij elke render een nieuwe onPreview mee. Hangt de preview daarvan
  // af, dan herstart elke render de berekening en staat "Formule gebruiken" net
  // tijdens de klik uitgeschakeld, waardoor de klik verloren gaat.
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const canPreview = Boolean(onPreview);
  const [body, setBody] = useState('');
  const [rounding, setRounding] = useState<FormulaRounding>('none');
  const [decimals, setDecimals] = useState(0);
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [search, setSearch] = useState('');
  const [ruleFilter, setRuleFilter] = useState<RuleFilter>('all');

  useEffect(() => {
    if (open && !wasOpen.current) {
      const parsed = parseFormulaEditorValue(value);
      setBody(parsed.body === '0' && !value.trim().startsWith('=') ? '' : parsed.body);
      setRounding(parsed.rounding);
      setDecimals(parsed.decimals);
      setSearch('');
      setRuleFilter('all');
      setPreview(
        initialPreview?.error
          ? { status: 'invalid', ...initialPreview }
          : initialPreview?.hours !== undefined
            ? { status: 'valid', ...initialPreview, beforeHours: initialPreview.hours }
            : { status: 'idle' }
      );
    }
    wasOpen.current = open;
  }, [initialPreview, open, value]);

  const formula = useMemo(
    () => buildFormulaEditorValue(body, rounding, decimals),
    [body, rounding, decimals]
  );

  useEffect(() => {
    const onPreview = onPreviewRef.current;
    if (!open || !formula || !canPreview || !onPreview) {
      if (!formula) setPreview({ status: 'idle' });
      return;
    }

    const requestId = ++previewRequest.current;
    setPreview({ status: 'loading' });
    const timer = window.setTimeout(() => {
      const rawFormula = `=${body.trim()}`;
      const finalRequest = onPreview(formula);
      const beforeRequest = rounding === 'none' ? finalRequest : onPreview(rawFormula);
      Promise.all([finalRequest, beforeRequest])
        .then(([result, before]) => {
          if (previewRequest.current !== requestId) return;
          setPreview(
            result.error
              ? { status: 'invalid', ...result, beforeHours: before.hours }
              : { status: 'valid', ...result, beforeHours: before.hours }
          );
        })
        .catch(() => {
          if (previewRequest.current === requestId) setPreview({ status: 'unavailable' });
        });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [body, canPreview, formula, open, rounding]);

  const replaceSelection = (replacement: string, caretBack = 0) => {
    const field = inputRef.current;
    const start = field?.selectionStart ?? body.length;
    const end = field?.selectionEnd ?? start;
    const next = `${body.slice(0, start)}${replacement}${body.slice(end)}`;
    setBody(next);

    window.requestAnimationFrame(() => {
      const caret = start + replacement.length - caretBack;
      field?.focus();
      field?.setSelectionRange(caret, caret);
    });
  };

  const addRule = (code: string) => {
    const field = inputRef.current;
    const start = field?.selectionStart ?? body.length;
    const end = field?.selectionEnd ?? start;
    if (start !== end || !body.trim() || /[(,+\-*/]\s*$/.test(body.slice(0, start))) {
      replaceSelection(code);
      return;
    }
    replaceSelection(` + ${code}`);
  };

  const wrapSelection = (functionName: string) => {
    const field = inputRef.current;
    const start = field?.selectionStart ?? 0;
    const end = field?.selectionEnd ?? body.length;
    const selected = body.slice(start, end) || body;

    if (!selected) {
      const template = functionName === 'POSITIVE' ? 'MAX(0,0)' : `${functionName}(0)`;
      setBody(template);
      window.requestAnimationFrame(() => {
        const placeholder = template.indexOf('0');
        field?.focus();
        field?.setSelectionRange(placeholder, placeholder + 1);
      });
      return;
    }

    const replacement =
      functionName === 'POSITIVE' ? `MAX(${selected},0)` : `${functionName}(${selected})`;
    if (selected === body) setBody(replacement);
    else setBody(`${body.slice(0, start)}${replacement}${body.slice(end)}`);

    window.requestAnimationFrame(() => {
      const caret = (selected === body ? 0 : start) + replacement.length;
      field?.focus();
      field?.setSelectionRange(caret, caret);
    });
  };

  const counts = useMemo(
    () => ({
      manual: options.filter((option) => option.kind !== 'formula').length,
      formula: options.filter((option) => option.kind === 'formula').length,
    }),
    [options]
  );

  const filteredOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return options.filter((option) => {
      if (ruleFilter !== 'all' && option.kind !== ruleFilter) return false;
      return (
        !needle ||
        option.description.toLowerCase().includes(needle) ||
        option.code.toLowerCase().includes(needle)
      );
    });
  }, [options, ruleFilter, search]);

  const labels = useMemo(
    () => new Map(options.map((option) => [option.code, option.description || option.code])),
    [options]
  );

  const invalid = preview.status === 'invalid';
  const waiting = preview.status === 'loading';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="grid h-[min(88vh,50rem)] w-[min(94vw,78rem)] max-w-none grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4 pr-14">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-info text-info-foreground">
              <FunctionSquare className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <DialogTitle className="truncate text-lg">
                Formule voor {lineLabel || 'deze regel'}
              </DialogTitle>
              <DialogDescription className="mt-1 truncate text-xs">
                {[variantName && `Variant ${variantName}`, rateLabel].filter(Boolean).join(' · ') ||
                  'Bouw de berekening op en kies de afronding'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="grid min-h-0 overflow-y-auto lg:grid-cols-[22rem_minmax(28rem,1fr)_20rem] lg:overflow-hidden">
          <aside className="flex min-h-[28rem] min-w-0 flex-col border-b bg-card lg:min-h-0 lg:border-b-0 lg:border-r">
            <div className="space-y-3 border-b p-5">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <h3 className="font-semibold">Regels toevoegen</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Klik op een regel om hem in de berekening te zetten.
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {options.length} beschikbaar
                </span>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="h-9 pl-9 text-xs"
                  placeholder="Zoek op naam of code…"
                  aria-label="Regels zoeken"
                />
              </div>
              <div
                className="grid grid-cols-3 rounded-md bg-muted p-1"
                role="tablist"
                aria-label="Regeltype"
              >
                {(
                  [
                    ['all', `Alle ${options.length}`],
                    ['manual', `Handmatig ${counts.manual}`],
                    ['formula', `Formules ${counts.formula}`],
                  ] as const
                ).map(([filter, label]) => (
                  <button
                    key={filter}
                    type="button"
                    role="tab"
                    aria-selected={ruleFilter === filter}
                    className={cn(
                      'rounded px-2 py-1.5 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      ruleFilter === filter
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                    onClick={() => setRuleFilter(filter)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="scrollbar-thin min-h-0 flex-1 space-y-2 overflow-y-auto p-5">
              {filteredOptions.map((option) => {
                const selected = codePattern(option.code).test(body);
                const derived = option.kind === 'formula';
                return (
                  <button
                    key={option.code}
                    type="button"
                    className={cn(
                      'group flex w-full min-w-0 items-center gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                      selected
                        ? 'border-info bg-info/10'
                        : 'bg-card hover:border-info/50 hover:bg-muted/60'
                    )}
                    onClick={() => addRule(option.code)}
                  >
                    <span
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded text-[10px] font-bold',
                        derived
                          ? 'bg-info text-info-foreground'
                          : 'bg-secondary text-secondary-foreground'
                      )}
                    >
                      {derived ? (
                        <FunctionSquare className="h-3.5 w-3.5" />
                      ) : (
                        <Hash className="h-3.5 w-3.5" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-semibold">
                        {option.description}
                      </span>
                      <code className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                        {option.code}
                      </code>
                    </span>
                    {option.hours !== undefined && (
                      <span className="shrink-0 text-xs font-semibold tabular-nums">
                        {new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(
                          option.hours
                        )}{' '}
                        u
                      </span>
                    )}
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded border bg-card text-muted-foreground group-hover:text-foreground">
                      {selected ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <Plus className="h-3.5 w-3.5" />
                      )}
                    </span>
                  </button>
                );
              })}
              {!filteredOptions.length && (
                <p className="rounded-md border border-dashed px-3 py-5 text-center text-xs leading-relaxed text-muted-foreground">
                  Geen regels gevonden. Pas je zoekopdracht of filter aan.
                </p>
              )}
            </div>
          </aside>

          <main className="scrollbar-thin min-w-0 overflow-visible p-6 lg:overflow-y-auto">
            <div className="space-y-6">
              <section aria-labelledby="formula-calculation-heading" className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 id="formula-calculation-heading" className="font-semibold">
                      Berekening
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Klik links op een regel of typ zelf: getallen, + − × ÷ en haakjes.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => setBody('')}
                    disabled={!body}
                  >
                    Alles wissen
                  </Button>
                </div>

                <div
                  className={cn(
                    'flex items-start gap-2 rounded-md border bg-card px-4 py-3 transition-colors focus-within:border-info focus-within:ring-2 focus-within:ring-info/15',
                    invalid &&
                      'border-destructive focus-within:border-destructive focus-within:ring-destructive/15'
                  )}
                  onClick={(event) => {
                    if (event.target === event.currentTarget) inputRef.current?.focus();
                  }}
                >
                  <span className="text-base font-bold leading-8 text-info" aria-hidden="true">
                    =
                  </span>
                  <FormulaExpressionField
                    ref={inputRef}
                    id="formula-expression"
                    aria-label="Berekening"
                    value={body}
                    onChange={(next) => setBody(next.replace(/^\s*=/, ''))}
                    labels={labels}
                    invalid={invalid}
                    placeholder="Kies links een regel of typ een getal"
                    className="min-w-0 flex-1 text-sm"
                  />
                </div>
              </section>

              <section aria-labelledby="formula-operators-heading" className="space-y-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h3
                    id="formula-operators-heading"
                    className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    Rekenen
                  </h3>
                  <span className="text-xs text-muted-foreground">Klik om toe te voegen</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {operators.map((operator) => (
                    <Button
                      key={operator.label}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => replaceSelection(operator.token, operator.caretBack)}
                    >
                      <span className="font-mono text-sm" aria-hidden="true">
                        {operator.symbol}
                      </span>
                      {operator.label}
                    </Button>
                  ))}
                </div>
              </section>

              <section aria-labelledby="formula-helpers-heading" className="space-y-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h3
                    id="formula-helpers-heading"
                    className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    Helperfuncties
                  </h3>
                  <span className="text-xs text-muted-foreground">
                    Geen formules uit het hoofd leren
                  </span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {helpers.map((helper, index) => {
                    const Icon = helper.icon;
                    return (
                      <button
                        key={helper.functionName}
                        type="button"
                        className={cn(
                          'flex min-h-14 items-start gap-2.5 rounded-md border px-3 py-2.5 text-left transition-colors hover:border-info/60 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                          index === 0 && 'border-info bg-info/10'
                        )}
                        onClick={() => wrapSelection(helper.functionName)}
                      >
                        <Icon
                          className={cn(
                            'mt-0.5 h-4 w-4 shrink-0',
                            index === 0 ? 'text-info' : 'text-foreground'
                          )}
                        />
                        <span>
                          <span className="block text-xs font-semibold">{helper.label}</span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            {helper.description}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          </main>

          <aside className="scrollbar-thin min-w-0 overflow-visible border-t bg-muted/60 p-5 lg:overflow-y-auto lg:border-l lg:border-t-0">
            <section aria-labelledby="formula-rounding-heading" className="space-y-4">
              <div>
                <h3 id="formula-rounding-heading" className="font-semibold">
                  Afronding
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  Bepaal hoe de berekende uren worden afgerond.
                </p>
              </div>
              <div
                role="radiogroup"
                aria-labelledby="formula-rounding-heading"
                className="space-y-2"
              >
                {roundingOptions.map((option) => {
                  const selected = rounding === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-md border bg-card px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                        selected ? 'border-info bg-info/10' : 'hover:border-info/50'
                      )}
                      onClick={() => setRounding(option.value)}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                          selected ? 'border-info' : 'border-muted-foreground/60'
                        )}
                      >
                        {selected && <span className="h-2 w-2 rounded-full bg-info" />}
                      </span>
                      <span>
                        <span className="block text-xs font-semibold">{option.label}</span>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">
                          {option.example}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className={cn('space-y-2 pt-1', rounding !== 'nearest' && 'opacity-50')}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Precisie
                  </span>
                  {rounding !== 'nearest' && (
                    <span className="text-[10px] text-muted-foreground">Bij dichtstbij</span>
                  )}
                </div>
                <div className="grid grid-cols-3 rounded-md bg-secondary p-1">
                  {[0, 1, 2].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      disabled={rounding !== 'nearest'}
                      aria-pressed={decimals === amount}
                      className={cn(
                        'rounded px-1 py-1.5 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        decimals === amount
                          ? 'bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                      onClick={() => setDecimals(amount)}
                    >
                      {amount === 0 ? 'Hele uren' : amount === 1 ? '1 decimaal' : '2 decimalen'}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section aria-live="polite" aria-label="Live formule-uitkomst" className="mt-6">
              <div
                className={cn(
                  'rounded-md border bg-card p-4',
                  preview.status === 'valid' && 'border-success/30 bg-success/5',
                  preview.status === 'invalid' && 'border-destructive/40 bg-destructive/5'
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Live uitkomst
                  </h3>
                  {preview.status === 'valid' && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-success">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Geldige formule
                    </span>
                  )}
                </div>
                {preview.status === 'loading' && (
                  <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                    Uitkomst berekenen…
                  </p>
                )}
                {preview.status === 'valid' && (
                  <>
                    <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                      <div>
                        <p className="text-[10px] text-muted-foreground">Voor afronden</p>
                        <p className="mt-1 text-sm tabular-nums">
                          {formatHours(preview.beforeHours)}
                        </p>
                      </div>
                      <span className="pb-0.5 text-muted-foreground" aria-hidden="true">
                        →
                      </span>
                      <div className="text-right">
                        <p className="text-[10px] text-muted-foreground">Na afronden</p>
                        <p className="mt-1 text-xl font-semibold tabular-nums">
                          {formatHours(preview.hours)}
                        </p>
                      </div>
                    </div>
                    {preview.amount && (
                      <p className="mt-3 border-t border-success/20 pt-3 text-xs text-muted-foreground">
                        Waarde in offerte:{' '}
                        <strong className="font-semibold text-foreground">{preview.amount}</strong>
                      </p>
                    )}
                  </>
                )}
                {preview.status === 'invalid' && (
                  <div className="mt-4 flex items-start gap-2 text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Controleer de formule</p>
                      <p className="mt-1 text-xs leading-relaxed">{preview.error}</p>
                    </div>
                  </div>
                )}
                {preview.status === 'unavailable' && (
                  <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                    De live uitkomst is nu niet beschikbaar. Probeer het opnieuw voordat je de
                    formule toepast.
                  </p>
                )}
                {preview.status === 'idle' && (
                  <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                    Kies een regel om de uitkomst te zien.
                  </p>
                )}
              </div>
            </section>
          </aside>
        </div>

        <DialogFooter className="items-center justify-between gap-4 border-t bg-card px-6 py-3 sm:space-x-0">
          <div className="mr-auto hidden items-center gap-2 text-xs text-muted-foreground sm:flex">
            {preview.status === 'valid' ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-success" />
                Geen kringverwijzingen gevonden
              </>
            ) : preview.status === 'invalid' ? (
              <>
                <AlertCircle className="h-4 w-4 text-destructive" />
                Los de formulefout op om verder te gaan
              </>
            ) : null}
          </div>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuleren
          </Button>
          <Button
            type="button"
            disabled={!formula || invalid || waiting || preview.status === 'unavailable'}
            onClick={() => {
              onApply(formula);
              onOpenChange(false);
            }}
          >
            Formule gebruiken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FormulaEditorDialog;
